import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { MONTHS, toISO, dEN, todayISO } from "@/lib/format";
import { Card } from "@/components/admin/common";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const COLOR = { free: "bg-emerald-100 text-emerald-900", partial: "bg-amber-200 text-amber-900", full: "bg-rose-300 text-rose-950" };
const WEEK = ["M", "T", "W", "T", "F", "S", "S"];

const monthCells = (y, m) => {
  const first = new Date(y, m, 1);
  const pad = (first.getDay() + 6) % 7;
  const n = new Date(y, m + 1, 0).getDate();
  return [...Array(pad).fill(null), ...Array.from({ length: n }, (_, i) => toISO(new Date(y, m, i + 1)))];
};

const MonthGrid = ({ y, m, days, onPick, big }) => (
  <div>
    <div className={`grid grid-cols-7 ${big ? "gap-1.5" : "gap-0.5"}`}>
      {WEEK.map((w, i) => <span key={i} className="text-[10px] text-center text-slate-400 font-semibold">{w}</span>)}
      {monthCells(y, m).map((d, i) => {
        if (!d) return <span key={i} />;
        const info = days[d];
        const st = info?.status || "free";
        return (
          <button key={d} onClick={() => onPick(d)} data-testid={`cal-day-${d}`}
            className={`${COLOR[st]} ${big ? "h-14 sm:h-16 rounded-xl text-sm p-1.5 flex flex-col items-start" : "aspect-square rounded-[4px] text-[9px]"} ${d === todayISO() ? "ring-2 ring-ink" : ""} font-semibold hover:opacity-80`}>
            {big ? <><span>{Number(d.slice(8))}</span>{info && <span className="text-[11px] font-bold mt-auto">{info.pax} pax</span>}</> : Number(d.slice(8))}
          </button>
        );
      })}
    </div>
  </div>
);

const DayDialog = ({ date, boatId, onClose }) => {
  const [items, setItems] = useState(null);
  useEffect(() => {
    if (!date) return;
    setItems(null);
    api.get("/admin/calendar/day", { params: { date, boat_id: boatId } }).then((r) => setItems(r.data));
  }, [date, boatId]);
  return (
    <Dialog open={!!date} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="font-admin max-h-[85vh] overflow-y-auto" data-testid="cal-day-dialog">
        <DialogHeader><DialogTitle>{date && dEN(date)}</DialogTitle></DialogHeader>
        {!items ? <p className="text-slate-500">Loading…</p> : !items.length ? <p className="text-slate-500" data-testid="cal-day-empty">No bookings — fully free.</p> : items.map((b) => (
          <Link key={b.id} to={`/admin/bookings/${b.id}`} className="block rounded-xl border border-border p-3 hover:bg-sand" data-testid={`cal-day-booking-${b.id}`}>
            <p className="font-bold">{b.customer_name} · {b.trip_type}</p>
            <p className="text-sm text-slate-500">{b.code} · {b.boat_name || "No boat"} · {b.pax} pax · {dEN(b.start_date)} – {dEN(b.end_date)}</p>
            {b.guests.length > 0 && <p className="text-sm mt-1">{b.guests.map((g) => g.full_name).join(", ")}</p>}
          </Link>
        ))}
      </DialogContent>
    </Dialog>
  );
};

export default function AvailabilityCalendar({ defaultView = "month" }) {
  const now = new Date();
  const [view, setView] = useState(defaultView);
  const [y, setY] = useState(now.getFullYear());
  const [m, setM] = useState(now.getMonth());
  const [boats, setBoats] = useState([]);
  const [boatId, setBoatId] = useState("all");
  const [data, setData] = useState({ days: {}, capacity: 0 });
  const [pick, setPick] = useState(null);

  useEffect(() => { api.get("/admin/boats").then((r) => setBoats(r.data)); }, []);
  const [start, end] = useMemo(() => (view === "year" ? [`${y}-01-01`, `${y}-12-31`] : [toISO(new Date(y, m, 1)), toISO(new Date(y, m + 1, 0))]), [view, y, m]);
  useEffect(() => {
    api.get("/admin/calendar", { params: { start, end, boat_id: boatId === "all" ? "" : boatId } }).then((r) => setData(r.data));
  }, [start, end, boatId]);

  const shift = (d) => {
    if (view === "year") return setY(y + d);
    const nm = m + d;
    setY(y + Math.floor(nm / 12));
    setM(((nm % 12) + 12) % 12);
  };

  return (
    <Card className="p-4 sm:p-5 space-y-4" data-testid="availability-calendar">
      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => shift(-1)} className="w-10 h-10 rounded-xl border border-border grid place-items-center" data-testid="cal-prev" aria-label="Previous"><ChevronLeft className="w-5 h-5" /></button>
        <p className="font-extrabold text-lg min-w-[130px] text-center" data-testid="cal-title">{view === "year" ? y : `${MONTHS[m]} ${y}`}</p>
        <button onClick={() => shift(1)} className="w-10 h-10 rounded-xl border border-border grid place-items-center" data-testid="cal-next" aria-label="Next"><ChevronRight className="w-5 h-5" /></button>
        <div className="flex bg-sand rounded-xl p-1 ml-auto">
          {["month", "year"].map((v) => (
            <button key={v} onClick={() => setView(v)} data-testid={`cal-view-${v}`} className={`px-3 h-8 rounded-lg text-sm font-semibold capitalize ${view === v ? "bg-white shadow-sm" : "text-slate-600"}`}>{v}</button>
          ))}
        </div>
        <Select value={boatId} onValueChange={setBoatId}>
          <SelectTrigger className="w-44 h-10" data-testid="cal-boat-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All boats</SelectItem>
            {boats.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="flex flex-wrap gap-4 text-xs font-semibold text-slate-600">
        <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded bg-emerald-100" />Free</span>
        <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded bg-amber-200" />Partly booked</span>
        <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded bg-rose-300" />Full</span>
        <span className="ml-auto" data-testid="cal-capacity">Capacity: {data.capacity || "not set"} pax/day</span>
      </div>
      {view === "month" ? <MonthGrid y={y} m={m} days={data.days} onPick={setPick} big /> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {MONTHS.map((name, i) => (
            <div key={name}>
              <button className="text-sm font-bold mb-1 hover:underline" onClick={() => { setM(i); setView("month"); }} data-testid={`cal-month-${i}`}>{name}</button>
              <MonthGrid y={y} m={i} days={data.days} onPick={setPick} />
            </div>
          ))}
        </div>
      )}
      <DayDialog date={pick} boatId={boatId === "all" ? "" : boatId} onClose={() => setPick(null)} />
    </Card>
  );
}
